/**
 * defaults.ts — Illustrative default simulation parameters.
 *
 * These are DEMO / EMPTY-STATE values used when no user input or LLM scenario
 * has been provided. They represent a plausible generic American household
 * (not any real person). All values are round numbers for clarity.
 *
 * HC enforced: [HC-DEFAULTS-IMMUTABLE] — no bare numeric literals in engine/*.ts.
 * HC enforced: [HC-NO-PERSONAL-DATA] — no real personal financial data here.
 *
 * Tax constants: IRS Publication 15 (2024) and state DOR (2024).
 * Review annually against IRS Rev. Proc. for COLA adjustments.
 */

// ─── Demo income profile ──────────────────────────────────────────────────────
export const SALARY_BASE = 100_000           // Illustrative W-2 base salary
export const BONUS_CASH = 20_000             // Illustrative annual cash bonus
export const BUSINESS_INCOME_ANNUAL = 20_000 // Illustrative self-employment gross
export const RSU_CURRENT = 50_000            // Illustrative RSU vest (current year)
export const RSU_ONGOING = 75_000            // Illustrative RSU vest (steady state)

// ─── Demo secondary income / career scenario ─────────────────────────────────
export const PARTNER_SALARY = 140_000
export const PARTNER_BONUS = 30_000
export const PARTNER_RSU = 100_000
export const PARTNER_BUSINESS_INCOME = 20_000

// ─── Demo starting assets: Liquid ────────────────────────────────────────────
export const CASH_FLOAT = 25_000
export const TAXABLE_BROKERAGE = 75_000
export const LIQUID_ASSETS_TOTAL = 100_000   // CASH_FLOAT + TAXABLE_BROKERAGE

// ─── Demo starting assets: Retirement ────────────────────────────────────────
export const RETIREMENT_OWN = 150_000
export const RETIREMENT_SPOUSE = 75_000
export const RETIREMENT_TOTAL = 225_000

// ─── Demo starting assets: Primary home ──────────────────────────────────────
export const PRIMARY_HOME_VALUE = 400_000
export const PRIMARY_LOAN_BALANCE = 300_000
export const PRIMARY_LOAN_RATE = 0.065            // 6.5% fixed (illustrative current market rate)
export const PRIMARY_HOME_EQUITY = 100_000
export const PRIMARY_PI_MONTHLY = 1_896           // P&I at 6.5% on $300k, 30yr
export const PRIMARY_PI_ANNUAL = 22_752
export const PRIMARY_LOAN_TERM_YEARS = 30

// ─── Demo starting assets: Secondary property (optional) ─────────────────────
export const SECONDARY_HOME_VALUE = 300_000
export const SECONDARY_LOAN_BALANCE = 240_000
export const SECONDARY_LOAN_RATE = 0.07
export const SECONDARY_HOME_EQUITY = 60_000
export const SECONDARY_PI_MONTHLY = 1_597
export const SECONDARY_PI_ANNUAL = 19_164
export const SECONDARY_LOAN_TERM_YEARS = 30

export const REAL_ESTATE_EQUITY_TOTAL = 160_000   // Primary + Secondary equity

// ─── Demo starting net worth ──────────────────────────────────────────────────
export const NET_WORTH_INCLUDED = 485_000          // Liquid + Retirement + Real estate equity

// ─── Demo annual expenses ─────────────────────────────────────────────────────
export const PRIMARY_HOUSING_MONTHLY = 2_200       // Mortgage P+I + tax + insurance
export const PRIMARY_HOUSING_ANNUAL = 26_400

export const SECONDARY_CARRY_MONTHLY = 2_000
export const SECONDARY_CARRY_ANNUAL = 24_000

// Secondary property rental scenario (illustrative)
export const SECONDARY_RENT_MONTHLY = 2_000
export const SECONDARY_RENT_ANNUAL = 24_000
export const SECONDARY_RENTAL_MGMT_RATE = 0.10
export const SECONDARY_RENTAL_MAINTENANCE_RATE = 0.015
export const SECONDARY_RENTAL_CAPEX_RATE = 0.01
export const SECONDARY_RENTAL_VACANCY_RATE = 0.05
export const SECONDARY_RENTAL_INSURANCE_EXTRA = 500
export const SECONDARY_HOME_VALUE_FOR_COSTS = SECONDARY_HOME_VALUE

// ─── Demo discretionary + misc expenses ──────────────────────────────────────
export const DISCRETIONARY_ANNUAL_BASE = 40_000
export const CHILDCARE_MONTHLY = 1_500
export const CHILDCARE_ANNUAL = 18_000
export const CHILDCARE_PHASE1_END_YEAR = 2027
export const CHILDCARE_PHASE2_START_YEAR = 2029
export const CHILDCARE_PHASE2_END_YEAR = 2031

// ─── Legacy constants preserved for engine compatibility (no personal data) ───
// These were scene-specific in 312-equity. Kept as aliases until engine is refactored.
export const ILLINOIS_HOUSING_ANNUAL = PRIMARY_HOUSING_ANNUAL
export const COLORADO_CARRY_ANNUAL = SECONDARY_CARRY_ANNUAL
export const COLORADO_STAGING_ANNUALIZED = 0         // no staging in generic demo
export const COMCAST_IL_ANNUAL = 1_200               // generic internet/TV
export const ATT_ANNUAL = 1_800                      // generic phone
export const IL_MAINTENANCE_ANNUAL = 3_000           // generic maintenance
export const SUBSCRIPTIONS_ANNUAL = 600              // generic subscriptions
export const LIFE_INSURANCE_ANNUAL = 300             // generic life insurance
export const COLORADO_NET_CARRY_RENTED_ANNUAL = 0    // computed at runtime
export const COLORADO_NET_CARRY_RENTED_MONTHLY = 0
export const COLORADO_RENTAL_MGMT_RATE = SECONDARY_RENTAL_MGMT_RATE
export const COLORADO_RENTAL_MAINTENANCE_RATE = SECONDARY_RENTAL_MAINTENANCE_RATE
export const COLORADO_RENTAL_CAPEX_RATE = SECONDARY_RENTAL_CAPEX_RATE
export const COLORADO_RENTAL_VACANCY_RATE = SECONDARY_RENTAL_VACANCY_RATE
export const COLORADO_RENTAL_INSURANCE_EXTRA = SECONDARY_RENTAL_INSURANCE_EXTRA
export const COLORADO_HOME_VALUE_FOR_COSTS = SECONDARY_HOME_VALUE_FOR_COSTS
export const IL_MORTGAGE_PAYDOWN_TARGET = 750_000    // IRS deductibility cap (2024, public)
export const ILLINOIS_HOME_VALUE = PRIMARY_HOME_VALUE
export const ILLINOIS_LOAN_BALANCE = PRIMARY_LOAN_BALANCE
export const ILLINOIS_LOAN_RATE = PRIMARY_LOAN_RATE
export const ILLINOIS_LOAN_TERM_YEARS = PRIMARY_LOAN_TERM_YEARS
export const ILLINOIS_PI_MONTHLY = PRIMARY_PI_MONTHLY
export const ILLINOIS_PI_ANNUAL = PRIMARY_PI_ANNUAL
export const COLORADO_HOME_VALUE = SECONDARY_HOME_VALUE
export const COLORADO_LOAN_BALANCE = SECONDARY_LOAN_BALANCE
export const COLORADO_LOAN_RATE = SECONDARY_LOAN_RATE
export const COLORADO_HOME_EQUITY = SECONDARY_HOME_EQUITY
export const COLORADO_CARRY_MONTHLY = SECONDARY_CARRY_MONTHLY
export const COLORADO_RENT_MONTHLY = SECONDARY_RENT_MONTHLY
export const COLORADO_RENT_ANNUAL = SECONDARY_RENT_ANNUAL

// Boat (generic demo — optional asset)
export const BOAT_PAYMENT_MONTHLY = 0
export const BOAT_PAYMENT_ANNUAL = 0
export const BOAT_LOAN_PRINCIPAL = 0
export const BOAT_LOAN_RATE = 0.07
export const BOAT_START_YEAR = 2026
export const BOAT_YEAR_1_FRACTION = 1.0

// Business investment (generic demo)
export const EXISTING_BUSINESS_LOAN_PRINCIPAL = 0
export const BUSINESS_312_DEFAULT_LOAN_RATE = 0.055
export const BUSINESS_312_DEFAULT_LOAN_TERM_YEARS = 7
export const BUSINESS_312_DEFAULT_POST_LOAN_YIELD = 0.06

// ─── Federal income tax brackets 2024 (MFJ) ──────────────────────────────────
// Source: IRS Rev. Proc. 2023-34 (public data)
export const FEDERAL_STANDARD_DEDUCTION_MFJ = 29_200

export const FEDERAL_BRACKETS_MFJ: Array<{ rate: number; floor: number; ceiling: number }> = [
  { rate: 0.10, floor: 0,         ceiling: 23_200 },
  { rate: 0.12, floor: 23_200,    ceiling: 94_300 },
  { rate: 0.22, floor: 94_300,    ceiling: 201_050 },
  { rate: 0.24, floor: 201_050,   ceiling: 383_900 },
  { rate: 0.32, floor: 383_900,   ceiling: 487_450 },
  { rate: 0.35, floor: 487_450,   ceiling: 731_200 },
  { rate: 0.37, floor: 731_200,   ceiling: Infinity },
]

// ─── Federal long-term capital gains brackets 2024 (MFJ) ─────────────────────
export const LTCG_BRACKETS_MFJ: Array<{ rate: number; floor: number; ceiling: number }> = [
  { rate: 0.00, floor: 0,         ceiling: 94_050 },
  { rate: 0.15, floor: 94_050,    ceiling: 583_750 },
  { rate: 0.20, floor: 583_750,   ceiling: Infinity },
]

export const NIIT_RATE = 0.038
export const NIIT_THRESHOLD_MFJ = 250_000

// ─── State income tax (illustrative — IL flat rate) ───────────────────────────
// Source: IL DOR — flat rate, no brackets
export const IL_TAX_RATE = 0.0495

// ─── FICA 2024 (public IRS data) ─────────────────────────────────────────────
export const SS_RATE = 0.062
export const SS_WAGE_BASE = 176_100        // 2026 (IRS Rev Proc 2025-52)
export const MEDICARE_RATE = 0.0145
export const MEDICARE_ADDITIONAL_RATE = 0.009
export const MEDICARE_ADDITIONAL_THRESHOLD_MFJ = 200_000
export const SE_TAX_RATE = 0.153
export const SE_INCOME_FACTOR = 0.9235
export const SE_DEDUCTIBLE_FRACTION = 0.5

// ─── Retirement contribution limits 2024 (public IRS data) ───────────────────
export const RETIREMENT_401K_LIMIT = 23_000
export const RETIREMENT_CATCHUP = 7_500

// ─── Default simulation assumptions ──────────────────────────────────────────
export const DEFAULT_EQUITY_RETURN_MEAN = 0.08
export const DEFAULT_EQUITY_RETURN_STD = 0.16
export const DEFAULT_BOND_RETURN_MEAN = 0.04
export const DEFAULT_BOND_RETURN_STD = 0.06
export const DEFAULT_CASH_RATE = 0.04
export const DEFAULT_INFLATION_MEAN = 0.03
export const DEFAULT_INFLATION_STD = 0.01
export const DEFAULT_REAL_ESTATE_APPRECIATION = 0.04
export const DEFAULT_INCOME_GROWTH_RATE = 0.03
export const DEFAULT_RSU_GROWTH_RATE = 0.05
export const DEFAULT_PROJECTION_YEARS = 30
export const DEFAULT_ITERATIONS = 5_000
export const DEFAULT_RETIREMENT_TARGET = 2_000_000
export const DEFAULT_START_YEAR = 2026

// ─── Social Security — 2024 bend points & rules (SSA public data) ────────────
export const SS_BEND_POINT_1 = 1_174
export const SS_BEND_POINT_2 = 7_078
export const SS_FRA_AGE = 67
export const SS_EARLY_CLAIM_AGE = 62
export const SS_MAX_DELAY_AGE = 70
export const SS_DELAY_CREDIT_PER_YEAR = 0.08

// ─── Generic SS earnings history defaults ────────────────────────────────────
// LLM or user input will override for a real scenario.
export const USER_BIRTH_YEAR = 1985
export const USER_SS_MAX_CONTRIB_YEARS = 20
export const USER_SS_NEAR_MAX_CONTRIB_YEARS = 5
export const USER_SS_NEAR_MAX_FRACTION = 0.80

export const SPOUSE_BIRTH_YEAR = 1985
export const SPOUSE_SS_MAX_CONTRIB_YEARS = 10
export const SPOUSE_SS_NEAR_MAX_CONTRIB_YEARS = 5
export const SPOUSE_SS_NEAR_MAX_FRACTION = 0.80

// ─── Retirement phase defaults ────────────────────────────────────────────────
export const DEFAULT_RETIREMENT_YEAR = 2055
export const DEFAULT_LIFE_EXPECTANCY_AGE = 90
export const DEFAULT_SS_CLAIM_AGE_USER = 67
export const DEFAULT_SS_CLAIM_AGE_SPOUSE = 67
export const DEFAULT_POST_RETIREMENT_EXPENSES = 80_000

// ─── Math constants used in simulation engine ────────────────────────────────
// Box-Muller transform
export const BOX_MULLER_LOG_GUARD = 1e-10   // prevents log(0) in randomNormal
// Seeded PRNG constants for deterministic algorithmic event generation (LCG algorithm)
export const LCG_MULTIPLIER = 1664525
export const LCG_INCREMENT = 1013904223
export const LCG_MODULUS = 0xffffffff     // 2^32 - 1 (unsigned 32-bit integer max)
// Event ID generation: base-36 encoding for compact unique IDs
export const EVENT_ID_BASE = 36
export const EVENT_ID_SLICE_START = 2
export const EVENT_ID_SLICE_END = 6
// Percentile bands
export const P10 = 10
export const P25 = 25
export const P50 = 50
export const P75 = 75
export const P90 = 90
// Real estate appreciation std dev
export const RE_APPRECIATION_STD = 0.03
// Median portfolio return calculation helpers
export const HALF = 2   // used in Math.pow(..., HALF) and std dev formulas
// SS monthly conversion
export const MONTHS_PER_YEAR = 12

// ─── Event default parameters (used in events.ts defaults) ──────────────────
export const DEFAULT_JOB_LOSS_INCOME_FRACTION = 0.40   // UI replaces with 40% of wages
export const DEFAULT_MEDICAL_COST = 50_000
export const DEFAULT_MEDICAL_DEDUCTIBLE = 8_000
export const DEFAULT_MEDICAL_SPREAD_YEARS = 1
export const DEFAULT_CHILDCARE_END_YEAR_OFFSET = 5     // 5 years of daycare
export const DEFAULT_COLLEGE_END_YEAR_OFFSET = 4       // 4-year degree
export const DEFAULT_MARKET_LOSS_PCT = 0.35
export const DEFAULT_MARKET_RECOVERY_YEARS = 3
export const DEFAULT_MARKET_RETURN_PENALTY = 0.04
export const DEFAULT_HOME_PRICE = 400_000
export const DEFAULT_DOWN_PAYMENT_PCT = 0.20
export const DEFAULT_MORTGAGE_RATE = 0.065
export const DEFAULT_MORTGAGE_TERM_YEARS = 30
export const DEFAULT_DIVORCE_ASSET_FRACTION = 0.50

// SS bend-point formula fractions (SSA public)
export const SS_FACTOR_BELOW_BEND1 = 0.90
export const SS_FACTOR_BETWEEN_BENDS = 0.32
export const SS_FACTOR_ABOVE_BEND2 = 0.15
// Max taxable portion of SS benefits (IRS: up to 85% taxable above income thresholds)
export const SS_MAX_TAXABLE_FRACTION = 0.85
// SSA early-claiming reduction: 5/9% per month for first 36 months, 5/12% beyond
export const SS_EARLY_REDUCTION_FIRST_36 = 5 / 9       // percent per month (evaluated at import)
export const SS_EARLY_REDUCTION_BEYOND_36 = 5 / 12     // percent per month
export const SS_EARLY_REDUCTION_BREAKPOINT = 36        // months
export const SS_PCT_DIVISOR = 100                      // convert from percent to decimal
// AIME divisor components
export const SS_AIME_YEARS_STANDARD = 35               // SSA uses top 35 earning years
export const SS_AIME_NEAR_MAX_THRESHOLD = 6            // months component in AIME calculation
export const SS_MONTHS_ADJUSTMENT = 2                  // month-count adjustment in SS calculations

// ─── Life event probability model constants ──────────────────────────────────
// All sourced from US demographic data (BLS, SSA, NAR, KFF, AARP, Fed SCF, CDC)
export const LIFE_EVENT_GENERATION_GAP_YEARS = 27
export const LIFE_EVENT_JOB_LOSS_CYCLE_YEARS = 14
export const LIFE_EVENT_MAX_JOB_LOSSES = 3
export const LIFE_EVENT_MARKET_CRASH_CYCLE_YEARS = 8
export const LIFE_EVENT_MAX_MARKET_CRASHES = 5
export const LIFE_EVENT_MEDICAL_SPIKE_AGE = 60         // late-life medical cost inflection
export const LIFE_EVENT_DISABILITY_YEAR_PCT = 0.35     // disability strikes at ~35% of career
export const LIFE_EVENT_PARENT_CARE_TRIGGER_AGE = 76
export const LIFE_EVENT_PARENT_CARE_DURATION = 4
// Job loss breakpoints and probabilities
export const LIFE_EVENT_JL_BREAKPOINT_1 = 0.18
export const LIFE_EVENT_JL_BREAKPOINT_2 = 0.45
export const LIFE_EVENT_JL_BREAKPOINT_3 = 0.72
export const LIFE_EVENT_JL_PROB_1 = 0.85
export const LIFE_EVENT_JL_PROB_2 = 0.60
export const LIFE_EVENT_JL_PROB_3 = 0.35
export const LIFE_EVENT_JL_INCOME_FRACTION = 0.40     // ~40% wage replacement via UI
export const LIFE_EVENT_JL_DURATION_YEARS = 1
// Medical event age percentages
export const LIFE_EVENT_MEDICAL_YOUNG_PCT = 0.40
export const LIFE_EVENT_MEDICAL_OLD_PCT = 0.15
export const LIFE_EVENT_MEDICAL_YOUNG_AGE_CUTOFF = 50
// Disability
export const LIFE_EVENT_DISABILITY_INCOME_FRACTION = 0.50
export const LIFE_EVENT_DISABILITY_DURATION = 3
// Home purchase year fraction
export const LIFE_EVENT_HOME_YEAR_FRACTION = 0.6
export const LIFE_EVENT_MAX_RENTER_AGE = 55
// Projection guard values
export const LIFE_EVENT_END_GUARD_4 = 4
export const LIFE_EVENT_END_GUARD_5 = 5
export const LIFE_EVENT_GRANDCHILD_END_GUARD = 3
export const LIFE_EVENT_MEDICAL_LATE_END_GUARD = 4
export const LIFE_EVENT_MIN_PROJECTION_MEDICAL = 5
export const LIFE_EVENT_MIN_PROJECTION_BONUS = 3
export const LIFE_EVENT_MIN_PROJECTION_DISABILITY = 8
export const LIFE_EVENT_MIN_PROJECTION_HOME = 4
export const LIFE_EVENT_COLLEGE_END_GUARD = 4
// Rounding increments for derived values
export const LIFE_EVENT_HOME_PRICE_ROUNDING = 10_000   // round home price to nearest $10k
export const LIFE_EVENT_BONUS_ROUNDING = 1_000         // round bonus to nearest $1k
export const LIFE_EVENT_FATHER_LIFE_EXPECTANCY = 74    // CDC 2022 US male avg
export const LIFE_EVENT_MOTHER_LIFE_EXPECTANCY = 80    // CDC 2022 US female avg
export const LIFE_EVENT_FIRST_HOME_AGE = 35            // NAR 2024 median first-time buyer
export const LIFE_EVENT_HOME_PRICE_MULTIPLE = 4.5      // home price ≈ 4.5× annual income
export const LIFE_EVENT_MIN_HOME_PRICE = 200_000
export const LIFE_EVENT_BONUS_INCOME_THRESHOLD = 50_000
export const LIFE_EVENT_BONUS_PCT = 0.08               // WTW 2024: avg bonus ~8% of salary
export const LIFE_EVENT_BONUS_CYCLE_YEARS = 2          // modeled every 2 years
export const LIFE_EVENT_VEHICLE_YEAR_PCT = 0.22        // first major expense at 22% of horizon
export const LIFE_EVENT_VEHICLE_COST = 45_000          // AAA 2024 avg new vehicle + repair
export const LIFE_EVENT_VEHICLE2_COST = 50_000
export const LIFE_EVENT_VEHICLE_CYCLE_YEARS = 8
export const LIFE_EVENT_GRANDCHILD_AGE = 55            // approx age to have grandchildren
export const LIFE_EVENT_GRANDCHILD_COST = 15_000
export const LIFE_EVENT_GRANDCHILD_SPREAD = 3
export const LIFE_EVENT_COLLEGE_OFFSET_BASE = 17       // child starts college at ~age 17+
export const LIFE_EVENT_COLLEGE_OFFSET_STAGGER = 2     // stagger per sibling
export const LIFE_EVENT_INHERITANCE_HIGH_INCOME = 120_000  // income bracket thresholds
export const LIFE_EVENT_INHERITANCE_MID_INCOME  = 70_000
export const LIFE_EVENT_INHERITANCE_FATHER_HIGH = 90_000   // Fed Reserve SCF brackets
export const LIFE_EVENT_INHERITANCE_FATHER_MID  = 55_000
export const LIFE_EVENT_INHERITANCE_FATHER_LOW  = 30_000
export const LIFE_EVENT_INHERITANCE_MOTHER_HIGH = 70_000
export const LIFE_EVENT_INHERITANCE_MOTHER_MID  = 40_000
export const LIFE_EVENT_INHERITANCE_MOTHER_LOW  = 20_000
export const LIFE_EVENT_PARENT_CARE_EARLY_COST = 14_000    // pre-75 informal care costs
export const LIFE_EVENT_PARENT_CARE_LATE_COST  = 28_000    // post-75 formal care costs
export const LIFE_EVENT_MEDICAL_COST_INSURED = 85_000      // typical insured large event
export const LIFE_EVENT_MEDICAL_DEDUCTIBLE_MAX = 9_000     // KFF 2024 OOP max
export const LIFE_EVENT_MEDICAL_LATE_COST = 200_000        // late-life cumulative OOP
export const LIFE_EVENT_MEDICAL_LATE_DEDUCTIBLE = 12_000
export const LIFE_EVENT_MEDICAL_LATE_SPREAD = 3
export const LIFE_EVENT_CHILDCARE_ANNUAL = 21_000          // Care.com 2024 US avg
export const LIFE_EVENT_COLLEGE_ANNUAL = 35_000            // College Board 2024-25 avg
